import { v7 as uuidv7 } from 'uuid';
import { DateTime } from 'luxon';
import { type NotificationEntry, NotificationType } from './types';
import { DeviceMeasurementErrorDto, MessageInfoTypeDto } from '@/api/generator';
import { success, info, warning, error as errorIcon } from '@/components/icons/icons';
import { t } from 'i18next';
import { I18nKey } from '@/utils/typings';
import { extractErrorMessage } from '@/errorHandling/errorUtils';
import { ResponseError } from '@/api/generator/runtime';

export function notificationForError(error: Error): NotificationEntry {
  return {
    id: uuidv7(),
    type: 'error',
    time: DateTime.now(),
    title: t('ErrorTitle'),
    icon: errorIcon,
    message:
      error instanceof ResponseError
        ? extractErrorMessage(error)
        : error.message || t('Error.Category.Other.message'),
    stackTrace: error.stack,
  };
}

export function notificationForDeviceError(error: DeviceMeasurementErrorDto): NotificationEntry {
  const cause = t(error.cause as I18nKey, '', { code: error.code });
  const remedy = t(error.remedy as I18nKey);

  const message = getNotificationDetails(cause, remedy);

  const type = getNotificationType(error);

  return {
    id: uuidv7(),
    time: DateTime.now(),
    title: t(error.message as I18nKey),
    icon: getNotificationIcon(type),
    message,
    type,
  };
}

function getNotificationIcon(type: NotificationType) {
  const iconMap: Record<NotificationType, string> = {
    success,
    info,
    warning,
    error: errorIcon,
  };

  return iconMap[type ?? 'error'];
}

function getNotificationType(message: DeviceMeasurementErrorDto) {
  const typeMap: Record<MessageInfoTypeDto, NotificationType> = {
    [MessageInfoTypeDto.Info]: 'info',
    [MessageInfoTypeDto.Warning]: 'warning',
    [MessageInfoTypeDto.Error]: 'error',
  };

  return typeMap[message.messageInfoType ?? MessageInfoTypeDto.Error];
}

export function getNotificationDetails(
  cause: string | undefined,
  remedy: string | undefined,
): string | undefined {
  return cause && remedy ? `${cause} — ${remedy}` : (cause ?? remedy);
}
